import { useWorkActions } from "@/features/workByManagement/hooks/useWorkActions";
import { useWorkState } from "@/features/workByManagement/hooks/useWorkState";
import { usePub, useSub } from "@/hooks";
import { theme } from "@/styles/theme";
import styled from "@emotion/styled";
import { Checkbox, Flex, Text } from "@mantine/core";
import { useEffect, useRef, useState } from "react";
import { DefectTypeItem } from "../ui/DefectTypeSet";

type PageKeyType = 'equipment' | 'plan';

interface WorkTableHeadProps {
    defectTypes: DefectTypeItem[] | null;
    pageKey: PageKeyType;
}

export const WorkTableHead = ({ defectTypes, pageKey }: WorkTableHeadProps) => {
    const isPlan = pageKey === 'plan';

    const numRefs = isPlan ? 7 : 5;
    const refs = useRef<(HTMLTableHeaderCellElement | null)[]>([]);
    const [widths, setWidths] = useState<number[]>(new Array(numRefs).fill(0));

    const state = useWorkState();
    const actions = useWorkActions();
    const [allChecked, setAllChecked] = useState<boolean>(false);
    const [indeterminate, setIndeterminate] = useState<boolean>(false);
    const publish = usePub();

    // 상태 변경 시 동기화
    useEffect(() => {
        setAllChecked(state.works.length === state.tempWorks.length);
    }, [state.works, state.tempWorks]);

    useEffect(() => {
        // 각 ref에 대한 offsetWidth를 계산하고 상태로 저장
        const newWidths = refs.current.map((ref) => (ref ? ref.offsetWidth : 0));
        setWidths(newWidths);
    }, [state.date, state.works, state.tempWorks]);

    useSub('check', () => {
        setAllChecked(state.works.length === state.tempWorks.length);
    });

    useSub('unCheck', () => {
        setAllChecked(false);
    });

    return (
        <thead>
            <tr>
                <Th leftWidth={0} ref={(el) => (refs.current[0] = el)}>
                    <Checkbox
                        checked={allChecked}
                        indeterminate={indeterminate}
                        onClick={(e) => {
                            if (e.currentTarget.checked) {
                                publish('checkAll');
                                actions.setWorks((prevWorks) => state.tempWorks);
                                setAllChecked(true);
                            }
                            else {
                                publish('unCheckAll');
                                actions.setWorks((prevWorks) => []);
                                setAllChecked(false);
                            }
                        }}
                    />
                </Th>
                {
                    isPlan ? (
                        <Th leftWidth={widths.slice(0, isPlan ? 1 : 0).reduce((acc, width) => acc + width, 0)} ref={(el) => (isPlan ? refs.current[1] = el : el)}>
                            <Text fz="sm" fw={500} c="black" ta="center" span>
                                작업순서
                            </Text>
                        </Th>
                    ) : null
                }
                {
                    isPlan ? (
                        <Th leftWidth={widths.slice(0, isPlan ? 2 : 0).reduce((acc, width) => acc + width, 0)} ref={(el) => (isPlan ? refs.current[2] = el : el)}>
                            <Flex gap="md" justify="center" align="center" direction="column" wrap="wrap">
                                <Text fz="sm" fw={500} c="black" ta="center" span>
                                    최근 작업일
                                </Text>
                            </Flex>
                        </Th>
                    ) : null
                }
                <Th leftWidth={widths.slice(0, isPlan ? 3 : 1).reduce((acc, width) => acc + width, 0)} ref={(el) => (isPlan ? refs.current[3] = el : refs.current[1] = el)}>
                    <Flex gap="md" justify="center" align="center" direction="column" wrap="wrap">
                        <Text fz="sm" fw={500} c="black" ta="center">
                            라인명
                        </Text>
                        <Text fz="sm" fw={500} c="black" ta="center">
                            설비명
                        </Text>
                    </Flex>
                </Th>
                <Th leftWidth={widths.slice(0, isPlan ? 4 : 2).reduce((acc, width) => acc + width, 0)} ref={(el) => (isPlan ? refs.current[4] = el : refs.current[2] = el)}>
                    <Flex align="center" direction="column" wrap="wrap">
                        <Flex direction="column" align="flex-start" gap="sm" wrap="wrap">
                            <Text fz="sm" fw={500} c="black">
                                AI 예측 품목(라우팅코드)
                            </Text>
                            <Text fz="sm" fw={500} c="black">
                                품목명(품목코드)
                            </Text>
                            <Text fz="sm" fw={500} c="black" ta="center">
                                규격
                            </Text>
                            <Text fz="sm" fw={500} c="black" ta="center">
                                진행률
                            </Text>
                        </Flex>
                    </Flex>
                </Th>
                <Th leftWidth={widths.slice(0, isPlan ? 5 : 3).reduce((acc, width) => acc + width, 0)} ref={(el) => (isPlan ? refs.current[5] = el : refs.current[3] = el)}>
                    <Flex align="center" direction="column" wrap="wrap">
                        <Flex direction="column" align="flex-start" gap="sm" wrap="wrap">
                            <Flex gap="xs">
                                <Text fz="sm" fw={500} c={theme.colors?.blue?.[9]}>
                                    계획수량
                                </Text>
                                <Text>/</Text>
                                <Text fz="sm" fw={500} c={theme.colors?.orange?.[9]}>
                                    생산 잔량
                                </Text>
                            </Flex>
                            <Flex gap="xs">
                                <Text fz="sm" fw={500} c={theme.colors?.teal?.[9]} ta="center">
                                    총생산수량
                                </Text>
                                <Text>/</Text>
                                <Text fz="sm" fw={500} c={theme.colors?.red?.[9]} ta="center">
                                    불량수량
                                </Text>
                            </Flex>
                        </Flex>
                    </Flex>
                </Th>
                <Th leftWidth={widths.slice(0, isPlan ? 6 : 4).reduce((acc, width) => acc + width, 0)} ref={(el) => (isPlan ? refs.current[6] = el : refs.current[4] = el)}>
                    <Text fz="sm" fw={500} c="black" ta="center">
                        완료수량
                    </Text>
                </Th>
                {
                    defectTypes?.map((defect) => (
                        <th key={defect.value}>
                            <Text fz="sm" fw={500} c="black" ta="center">
                                {defect.label}
                            </Text>
                        </th>
                    )
                    )
                }
            </tr>
        </thead>
    )
}

const Th = styled.th<{ leftWidth?: number }>`
    position: sticky; 
    background-color: white;
    border-right: 0.0625rem solid rgb(222, 226, 230);
    border-bottom: 0.0625rem solid rgb(222, 226, 230);
    left: ${(props) => props.leftWidth ?? 0}px;
`;